import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { UserProfile, Block } from './types';
import { THEMES, LAYOUTS } from './types';

// ============================================
// User Profile Functions
// ============================================

export async function saveUserProfile(
  userId: string,
  profile: Partial<Omit<UserProfile, 'uid' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  const userRef = doc(db, 'users', userId);
  const existingDoc = await getDoc(userRef);

  if (existingDoc.exists()) {
    await updateDoc(userRef, {
      ...profile,
      updatedAt: serverTimestamp(),
    });
  } else {
    const defaultTheme = THEMES[0];
    const defaultLayout = LAYOUTS[0];
    
    await setDoc(userRef, {
      uid: userId,
      username: profile.username || '',
      displayName: profile.displayName || '',
      bio: profile.bio || '',
      avatarUrl: profile.avatarUrl || '',
      theme: profile.theme || defaultTheme,
      layout: profile.layout || defaultLayout,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  // If username is being set, also update the usernames collection
  if (profile.username) {
    const usernameRef = doc(db, 'usernames', profile.username.toLowerCase());
    await setDoc(usernameRef, { uid: userId });
  }
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const userRef = doc(db, 'users', userId);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();
  return {
    ...data,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
  } as UserProfile;
}

export function onUserProfileChange(
  userId: string,
  callback: (profile: UserProfile | null) => void
): Unsubscribe {
  const userRef = doc(db, 'users', userId);
  return onSnapshot(userRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }
    const data = snapshot.data();
    callback({
      ...data,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
    } as UserProfile);
  });
}

export async function getUsernameForUser(userId: string): Promise<string | null> {
  const profile = await getUserProfile(userId);
  return profile?.username || null;
}

// ============================================
// Block CRUD Functions
// ============================================

export async function createBlock(
  userId: string,
  block: Omit<Block, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const blocksRef = collection(db, 'users', userId, 'blocks');
  const docRef = await addDoc(blocksRef, {
    ...block,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateBlock(
  userId: string,
  blockId: string,
  data: Partial<Omit<Block, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  const blockRef = doc(db, 'users', userId, 'blocks', blockId);
  await updateDoc(blockRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBlock(userId: string, blockId: string): Promise<void> {
  const blockRef = doc(db, 'users', userId, 'blocks', blockId);
  await deleteDoc(blockRef);
}

export async function getBlocks(userId: string): Promise<Block[]> {
  const blocksRef = collection(db, 'users', userId, 'blocks');
  const q = query(blocksRef, orderBy('order', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
    } as Block;
  });
}

export function onBlocksChange(
  userId: string,
  callback: (blocks: Block[]) => void
): Unsubscribe {
  const blocksRef = collection(db, 'users', userId, 'blocks');
  const q = query(blocksRef, orderBy('order', 'asc'));

  return onSnapshot(q, (snapshot) => {
    const blocks = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(),
      } as Block;
    });
    callback(blocks);
  });
}
